import React from 'react';
import Icon from '../../../components/AppIcon';

const GoalComparison = ({ goals }) => {
  const formatIndianCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const calculateTotalRequired = () => {
    return goals?.reduce((sum, goal) => sum + goal?.targetAmount, 0);
  };

  const calculateTotalSavings = () => {
    return goals?.reduce((sum, goal) => sum + goal?.currentSavings, 0);
  };

  const calculateTotalMonthly = () => {
    return goals?.reduce((sum, goal) => sum + goal?.monthlySavingsRequired, 0);
  };

  const getGoalColor = (type) => {
    const colors = {
      car: 'bg-blue-500',
      home: 'bg-emerald-500',
      education: 'bg-purple-500',
      wedding: 'bg-pink-500',
      custom: 'bg-amber-500'
    };
    return colors?.[type] || 'bg-gray-500';
  };

  const sortedGoals = [...goals]?.sort((a, b) => b?.targetAmount - a?.targetAmount);

  if (goals?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-8 text-center">
        <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <p className="text-muted-foreground">No goals to compare</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="BarChart3" size={24} color="var(--color-primary)" />
        <h3 className="text-xl font-bold text-foreground">Goal Comparison</h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-primary/10 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Target</p>
          <p className="text-2xl font-bold text-primary">{formatIndianCurrency(calculateTotalRequired())}</p>
        </div>
        <div className="bg-accent/10 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Current Savings</p>
          <p className="text-2xl font-bold text-accent">{formatIndianCurrency(calculateTotalSavings())}</p>
        </div>
        <div className="bg-warning/10 rounded-lg p-4">
          <p className="text-sm text-muted-foreground mb-1">Monthly Required</p>
          <p className="text-2xl font-bold text-warning">{formatIndianCurrency(calculateTotalMonthly())}</p>
        </div>
      </div>
      <div className="space-y-4">
        {sortedGoals?.map((goal) => {
          const percentage = (goal?.targetAmount / calculateTotalRequired()) * 100;
          
          return (
            <div key={goal?.id}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${getGoalColor(goal?.type)}`} />
                  <span className="text-sm font-medium text-foreground">{goal?.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">{formatIndianCurrency(goal?.targetAmount)}</p>
                  <p className="text-xs text-muted-foreground">{percentage?.toFixed(1)}% of total</p>
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`${getGoalColor(goal?.type)} h-2 rounded-full transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-6 bg-muted/50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Resource Allocation</p>
            <p className="text-xs text-muted-foreground">
              Your goals require a total monthly savings of {formatIndianCurrency(calculateTotalMonthly())}. 
              Ensure this aligns with your income and other financial commitments.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalComparison;